<template>
  <div class="ecomac-logo" :class="containerClass">
    <!-- Image Logo -->
    <img 
      v-if="logoSrc && !imageError" 
      :src="logoSrc" 
      :alt="altText"
      :class="imageClass"
      @error="handleImageError"
    >
    
    <!-- Fallback Logo cuando no hay imagen o falló la carga -->
    <div v-else-if="!showText || imageError" class="logo-fallback" :class="fallbackClass">
      <svg class="w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <!-- Modern geometric logo design -->
        <defs>
          <linearGradient id="ecomac-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" :style="{ stopColor: variant === 'white' ? '#3B82F6' : '#1E40AF' }" />
            <stop offset="100%" :style="{ stopColor: variant === 'white' ? '#8B5CF6' : '#7C3AED' }" />
          </linearGradient>
          <linearGradient id="ecomac-accent" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" :style="{ stopColor: variant === 'white' ? '#10B981' : '#059669' }" />
            <stop offset="100%" :style="{ stopColor: variant === 'white' ? '#3B82F6' : '#1D4ED8' }" />
          </linearGradient>
        </defs>
        
        <!-- Background circle -->
        <circle cx="50" cy="50" r="45" fill="url(#ecomac-gradient)" opacity="0.1" />
        
        <!-- Letter E -->
        <path d="M25 25 L25 75 L70 75 M25 50 L60 50 M25 25 L65 25" 
              stroke="url(#ecomac-gradient)" 
              stroke-width="8" 
              stroke-linecap="round" 
              stroke-linejoin="round" 
              fill="none" />
        
        <!-- Accent dot -->
        <circle cx="75" cy="30" r="6" fill="url(#ecomac-accent)" />
      </svg>
    </div>
    
    <!-- Text content -->
    <div v-if="showText" class="logo-text" :class="textClass">
      <h1 class="font-bold" :class="titleClass">{{ title }}</h1>
      <p v-if="subtitle" class="font-light opacity-90" :class="subtitleClass">{{ subtitle }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import logoWhite from '../assets/ecomac-logo-white.webp';

const props = defineProps({
  variant: {
    type: String,
    default: 'white', // 'white' | 'blue'
    validator: (value) => ['white', 'blue'].includes(value)
  },
  size: {
    type: String,
    default: 'medium', // 'small' | 'medium' | 'large'
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  showText: {
    type: Boolean,
    default: true
  },
  title: {
    type: String,
    default: 'ECOMAC'
  },
  subtitle: {
    type: String,
    default: 'Portal de Soporte'
  },
  orientation: {
    type: String,
    default: 'horizontal', // 'horizontal' | 'vertical'
    validator: (value) => ['horizontal', 'vertical'].includes(value)
  }
});

const imageError = ref(false);

const logoSrc = computed(() => {
  if (imageError.value) {
    // Fallback to text-only if image fails
    return null;
  }
  
  switch (props.variant) {
    case 'white':
      return logoWhite;
    case 'blue':
    default:
      // Por ahora solo tenemos la versión blanca, se puede agregar la azul después
      return logoWhite;
  }
});

const altText = computed(() => `${props.title} Logo`);

const containerClass = computed(() => {
  const classes = ['flex items-center'];
  
  if (props.orientation === 'vertical') {
    classes.push('flex-col text-center');
  } else {
    classes.push('flex-row space-x-3');
  }
  
  return classes.join(' ');
});

const imageClass = computed(() => {
  const classes = ['object-contain'];
  
  switch (props.size) {
    case 'small':
      classes.push('h-8 w-auto max-w-none');
      break;
    case 'large':
      classes.push('h-16 w-auto max-w-none');
      break;
    default: // medium
      classes.push('h-12 w-auto max-w-none');
  }
  
  if (props.orientation === 'vertical') {
    classes.push('mb-2');
  }
  
  return classes.join(' ');
});

const textClass = computed(() => {
  const classes = [];
  
  if (props.variant === 'white') {
    classes.push('text-white');
  } else {
    classes.push('text-primary');
  }
  
  return classes.join(' ');
});

const titleClass = computed(() => {
  const classes = [];
  
  switch (props.size) {
    case 'small':
      classes.push('text-lg');
      break;
    case 'large':
      classes.push('text-4xl');
      break;
    default: // medium
      classes.push('text-2xl');
  }
  
  return classes.join(' ');
});

const subtitleClass = computed(() => {
  const classes = [];
  
  switch (props.size) {
    case 'small':
      classes.push('text-xs');
      break;
    case 'large':
      classes.push('text-lg');
      break;
    default: // medium
      classes.push('text-sm');
  }
  
  return classes.join(' ');
});

const fallbackClass = computed(() => {
  const classes = [];
  
  switch (props.size) {
    case 'small':
      classes.push('h-8 w-8');
      break;
    case 'large':
      classes.push('h-16 w-16');
      break;
    default: // medium
      classes.push('h-12 w-12');
  }
  
  if (props.orientation === 'vertical') {
    classes.push('mb-2');
  }
  
  return classes.join(' ');
});

const handleImageError = () => {
  imageError.value = true;
  console.warn('Error cargando el logotipo de Ecomac, usando fallback SVG');
};
</script>

<style scoped>
.ecomac-logo {
  user-select: none;
}

.logo-text {
  transition: all 0.3s ease;
}

.ecomac-logo:hover .logo-text {
  transform: translateY(-1px);
}

.logo-fallback {
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.ecomac-logo:hover .logo-fallback {
  transform: scale(1.05) rotate(2deg);
}

.logo-fallback svg {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}
</style>
